import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, GripVertical } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "@/lib/api";

const EMPTY = { orario: "", titolo: "", descrizione: "", ordine: 0 };

function SortableRow({ item, onEdit, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, borderColor: "#e3ddd2", zIndex: isDragging ? 30 : undefined, boxShadow: isDragging ? "0 10px 26px rgba(0,0,0,0.12)" : undefined };
  return (
    <div ref={setNodeRef} style={style} className="bg-white border rounded-2xl p-4 flex items-center gap-3" data-testid={`program-row-${item.id}`}>
      <button
        {...attributes}
        {...listeners}
        className="text-neutral-300 hover:text-neutral-500 cursor-grab active:cursor-grabbing touch-none shrink-0"
        aria-label="Trascina per riordinare"
        data-testid={`program-drag-${item.id}`}
      >
        <GripVertical size={18} />
      </button>
      <span className="font-wed text-lg w-16 shrink-0" style={{ color: "var(--wed-primary)" }}>{item.orario}</span>
      <div className="flex-1 min-w-0">
        <p className="font-medium">{item.titolo}</p>
        {item.descrizione && <p className="text-sm text-neutral-500 truncate">{item.descrizione}</p>}
      </div>
      <button onClick={() => onEdit(item)} className="p-1.5 text-neutral-400 hover:text-neutral-700 transition-colors duration-150" data-testid={`program-edit-${item.id}`}><Pencil size={15} /></button>
      <button onClick={() => onRemove(item.id)} className="p-1.5 text-neutral-400 hover:text-rose-500 transition-colors duration-150" data-testid={`program-delete-${item.id}`}><Trash2 size={15} /></button>
    </div>
  );
}

export default function ProgramTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = () => api.get("/owner/wedding").then((r) => setItems(r.data.program || []));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.orario || !form.titolo) return toast.error("Orario e titolo obbligatori");
    const payload = { ...form, ordine: Number(form.ordine) || items.length + 1 };
    try {
      if (editing === "new") await api.post("/owner/program", payload);
      else await api.put(`/owner/program/${editing}`, payload);
      toast.success("Programma aggiornato");
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  const remove = async (id) => {
    await api.delete(`/owner/program/${id}`);
    load();
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);
    api.put("/owner/program/reorder", { order: reordered.map((i) => i.id) })
      .then(() => toast.success("Ordine aggiornato"))
      .catch(() => { toast.error("Errore nel riordino"); load(); });
  };

  return (
    <div data-testid="program-tab">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-wed text-2xl">Programma della giornata</h2>
          <p className="text-sm text-neutral-500 mt-1">Trascina le voci per cambiarne l'ordine.</p>
        </div>
        <button className="btn-wed !py-2 !px-4 text-sm flex items-center gap-2" onClick={() => { setEditing("new"); setForm({ ...EMPTY, ordine: items.length + 1 }); }} data-testid="program-add-button">
          <Plus size={15} /> Aggiungi
        </button>
      </div>

      {editing && (
        <form onSubmit={save} className="bg-white border rounded-2xl p-5 mb-6 grid sm:grid-cols-2 gap-4" style={{ borderColor: "#e3ddd2" }} data-testid="program-form">
          <div className="sm:col-span-2 flex justify-between items-center">
            <h3 className="font-wed text-lg">{editing === "new" ? "Nuova voce" : "Modifica voce"}</h3>
            <button type="button" onClick={() => setEditing(null)} className="text-neutral-400 hover:text-neutral-700"><X size={18} /></button>
          </div>
          <input className="input-wed" type="time" value={form.orario} onChange={(e) => setForm({ ...form, orario: e.target.value })} data-testid="program-form-time" />
          <input className="input-wed" placeholder="Titolo *" value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} data-testid="program-form-title" />
          <input className="input-wed sm:col-span-2" placeholder="Descrizione" value={form.descrizione || ""} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} data-testid="program-form-desc" />
          <button type="submit" className="btn-wed sm:col-span-2" data-testid="program-form-save">Salva</button>
        </form>
      )}

      {items.length === 0 ? (
        <p className="text-center text-neutral-400 py-8">Nessuna voce nel programma</p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3" style={{ borderColor: "#e3ddd2" }} data-testid="program-list">
              {items.map((item) => (
                <SortableRow key={item.id} item={item} onEdit={(it) => { setEditing(it.id); setForm(it); }} onRemove={remove} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
